import React from 'react';
import { Intent, Button, ProgressBar, Callout, Divider } from '@blueprintjs/core';
import { captureException } from '@sentry/browser';
import { DatasetErrors, DatasetStatus } from '@retro-ui/api-contract';

import ContextualCard from '@f/components/shared/contextual-card/ContextualCard';
import ParameterList from '@f/components/shared/parameter-list/ParameterList';
import { saveText } from '@f/utils/export';
import { formatBytes } from '@f/utils/text';
import {
  StartingMaterialDatasetItem,
  MapDatasetItem,
} from '@f/services/starting-material/starting-material.types';

import UploadForm from './UploadForm';
import cls from './Collapse.module.scss';

interface CollapseBodyProps {
  item: StartingMaterialDatasetItem;
}

interface ParameterSchema {
  label: string | MapDatasetItem<React.ReactElement>;
  renderer: string | MapDatasetItem<string> | MapDatasetItem<React.ReactElement>;
}

interface ParameterListSchema {
  [columnIdx: number]: ParameterSchema;
}

const VISIBLE_ERROR_LIMIT = 10;

function parseErrors(errors: DatasetErrors): string[] {
  const parsingErrors = errors.csvErrors.map(errors => {
    switch (errors.errorType) {
      case 'UnequalLengths': {
        return `Error occured while parsing CSV at data row ${errors.line}.`;
      }
      case 'NoCsvHeader': {
        return 'CSV header not found.';
      }
    }

    return `Unknown CSV-related error occured at data row ${errors.line}.`;
  });

  const smilesErrors = errors.smilesErrors.map(warn => {
    switch (warn.errorType) {
      case 'BadSmiles': {
        return `Incorrect SMILES found: ${warn.smiles} at data row ${warn.line}.`;
      }
      case 'EmptySmiles': {
        return 'Empty SMILES found.';
      }
      case 'MultiPartCompound': {
        return `Multi-part SMILES found: ${warn.smiles} at data row ${warn.line}.`;
      }
      case 'CouldNotSerialize': {
        return `Error occured while parsing SMILES: ${warn.smiles} at data row ${warn.line}.`;
      }
    }

    return `Unknown SMILES-related error occured at data row ${warn.line}.`;
  });

  const limitExceededMessage = errors.errorLimitReached
    ? 'Further messages are not shown due to exceeded error limit.'
    : '';

  return [...parsingErrors, ...smilesErrors, limitExceededMessage].filter(Boolean);
}

const StyledParameterList = ({
  className = cls.parameterList,
  itemValueClassName = cls.parameterListItemValue,
  itemLabelClassName = cls.parameterListItemLabel,
  ...props
}) => (
  <ParameterList
    className={className}
    itemValueClassName={itemValueClassName}
    itemLabelClassName={itemLabelClassName}
    {...props}
  />
);

function AvailableItemCollapseBody(props: CollapseBodyProps) {
  const { item } = props;

  const parameterListSchema: ParameterListSchema = [
    { label: 'Total size', renderer: (item): string => formatBytes(item.size) },
    { label: 'Original file name', renderer: 'fileName' },
    { label: 'Created at', renderer: (item): string => new Date(item.createdAt).toLocaleString() },
    {
      label: 'Uploaded at',
      renderer: (item): string =>
        item.uploadedAt ? new Date(item.uploadedAt).toLocaleString() : 'n/a',
    },
  ];

  return (
    <>
      <StyledParameterList schema={parameterListSchema} data={item} />
      {/* <Divider className={cls.divider} />
      <Button outlined text="Download" icon="cloud-download" /> */}
    </>
  );
}

function ProcessingUploadCollapseBody(props: CollapseBodyProps) {
  const { item } = props;

  const parameterListSchema: ParameterListSchema = [
    { label: 'Total size', renderer: (item): string => formatBytes(item.size) },
    { label: 'Original file name', renderer: 'fileName' },
    { label: 'Created at', renderer: (item): string => new Date(item.createdAt).toLocaleString() },
    {
      label: 'Uploaded at',
      renderer: (item): string =>
        item.uploadedAt ? new Date(item.uploadedAt).toLocaleString() : 'n/a',
    },
  ];

  return <StyledParameterList schema={parameterListSchema} data={item} />;
}

function UploadingCollapseBody(props: CollapseBodyProps) {
  const { item } = props;

  const parameterListSchema: ParameterListSchema = [
    {
      label: 'Uploaded size',
      renderer: item => (
        <div className={cls.uploadedSizeContainer}>
          <span className={cls.uploadedSizeText}>{formatBytes(item.uploadedSize)}</span>
          <ProgressBar
            className={cls.uploadProgressBar}
            animate
            intent={Intent.WARNING}
            value={item.uploadedSize / item.size}
          />
        </div>
      ),
    },
    { label: 'Total size', renderer: (item): string => formatBytes(item.size) },
    { label: 'Original file name', renderer: 'fileName' },
    { label: 'Created at', renderer: (item): string => new Date(item.createdAt).toLocaleString() },
  ];

  return <StyledParameterList schema={parameterListSchema} data={item} />;
}

function UploadPausedItemCollapseBody(props: CollapseBodyProps) {
  const { item } = props;

  const parameterListSchema: ParameterListSchema = [
    {
      label: 'Uploaded size',
      renderer: item => (
        <div className={cls.uploadedSizeContainer}>
          <span className={cls.uploadedSizeText}>{formatBytes(item.uploadedSize)}</span>
          <ProgressBar
            className={cls.uploadProgressBar}
            stripes={false}
            intent={Intent.PRIMARY}
            value={item.uploadedSize / item.size}
          />
        </div>
      ),
    },
    { label: 'Total size', renderer: (item): string => formatBytes(item.size) },
    {
      label: 'Original file name',
      renderer: item => <span className={cls.fileNameParameter}>{item.fileName}</span>,
    },
    { label: 'Created at', renderer: (item): string => new Date(item.createdAt).toLocaleString() },
  ];

  return (
    <>
      <Callout className={cls.resumeUploadCallout} intent={Intent.PRIMARY} icon="outdated">
        File upload has been paused. In order to resume the upload, please submit the{' '}
        <strong>original</strong> file again.
      </Callout>
      <div className={cls.resumeUploadContainer}>
        <StyledParameterList
          className={cls.resumeUploadParameterList}
          schema={parameterListSchema}
          data={item}
        />
        <UploadForm
          className={cls.uploadForm}
          existingDatasetId={item.id}
          existingDatasetSize={item.size}
          submitButtonProps={{
            className: cls.uploadSubmitButton,
            text: 'Resume upload',
          }}
        ></UploadForm>
      </div>
    </>
  );
}

function UploadFailedItemCollapseBody(props: CollapseBodyProps) {
  const { item } = props;

  const parameterListSchema: ParameterListSchema = [
    { label: 'Total size', renderer: (item): string => formatBytes(item.size) },
    { label: 'Original file name', renderer: 'fileName' },
    { label: 'Created at', renderer: (item): string => new Date(item.createdAt).toLocaleString() },
  ];
  const parsedErrors = parseErrors(item.errors);
  const totalErrors = parsedErrors.length;
  const errorLimitExceeded = totalErrors > VISIBLE_ERROR_LIMIT;

  const downloadErrorLog = () => {
    const filename = `${item.name.toLowerCase().replaceAll(' ', '_')}.log`;
    const content = parsedErrors.join('\n');
    saveText(content, filename);
  };

  return (
    <div>
      <StyledParameterList schema={parameterListSchema} data={item} />
      <Divider className={cls.divider} />
      <div className={cls.errorContainer}>
        <Callout className={cls.errorCallout} intent={Intent.DANGER}>
          <p>
            File upload has failed due to <strong>{totalErrors}</strong> error
            {totalErrors > 1 && 's'}:
          </p>
          <code>
            <ul className={cls.errorList}>
              {parsedErrors.slice(0, 10).map(error => (
                <li>{error}</li>
              ))}
            </ul>
            {errorLimitExceeded && <span>[...]</span>}
          </code>

          <Button
            outlined
            text="Download error log"
            icon="cloud-download"
            onClick={downloadErrorLog}
          />
        </Callout>
        <Callout intent={Intent.PRIMARY}>
          Please correct the CSV content and submit the file again.
        </Callout>
      </div>
    </div>
  );
}

function UploadNotFoundItemCollapseBody(props: CollapseBodyProps) {
  const { item } = props;

  const parameterListSchema: ParameterListSchema = [
    { label: 'Total size', renderer: (item): string => formatBytes(item.size) },
    { label: 'Original file name', renderer: 'fileName' },
    { label: 'Created at', renderer: (item): string => new Date(item.createdAt).toLocaleString() },
  ];

  return (
    <div>
      <StyledParameterList schema={parameterListSchema} data={item} />
    </div>
  );
}

function getCollapseBody(item: StartingMaterialDatasetItem) {
  if (item.status === DatasetStatus.NOT_FOUND) {
    captureException(new Error(`Starting material dataset not found: ${item.id}`), {
      extra: {
        extendedId: item.extendedId,
      },
    });
  }

  switch (item.status) {
    case DatasetStatus.READY:
      return <AvailableItemCollapseBody item={item} />;
    case DatasetStatus.PROCESSING:
      return <ProcessingUploadCollapseBody item={item} />;
    case DatasetStatus.SENDING:
      return <UploadingCollapseBody item={item} />;
    case DatasetStatus.INCOMPLETE:
      return <UploadPausedItemCollapseBody item={item} />;
    case DatasetStatus.ERROR:
      return <UploadFailedItemCollapseBody item={item} />;
    case DatasetStatus.NOT_FOUND:
      return <UploadNotFoundItemCollapseBody item={item} />;
    default:
      return null;
  }
}

export default function (props: CollapseBodyProps) {
  const { item } = props;

  return (
    <ContextualCard className={cls.extendedInfoCard}>
      <ContextualCard.Main className={cls.extendedInfoContent}>
        {getCollapseBody(item)}
      </ContextualCard.Main>
      <ContextualCard.Floating className={cls.extendedInfoActions} />
    </ContextualCard>
  );
}
